/**
 * Tab drag-and-drop types and helpers.
 *
 * `DraggableTabData` is carried on every draggable tab handle.
 * `DroppableTabBarData` is carried on every panel's tab bar drop zone.
 * `computeDropIndex` converts pointer position into an insertion index.
 * `resolveDragEnd` is the pure handler for onDragEnd — testable without DOM.
 */

export type DraggableTabData = {
  type: 'tab';
  tabId: string;
  tabType?: string;
  fromPanelId: string;
  fromTabIndex: number;
};

export type DroppableTabBarData = {
  type: 'tabbar';
  panelId: string;
};

/**
 * Given the translated rect of the dragged item and an array of tab rects in
 * the target panel, returns the index at which the dragged tab should be
 * inserted. Falls back to appending at the end when measurement data is
 * unavailable.
 */
export function computeDropIndex(
  activeRect: Pick<DOMRect, 'left' | 'width'> | null,
  overRect: Pick<DOMRect, 'left' | 'width'> | null,
  tabRects: Pick<DOMRect, 'left' | 'width'>[],
): number {
  if (!activeRect || !overRect || tabRects.length === 0) return tabRects.length;
  const pointerX = activeRect.left + activeRect.width / 2;
  for (let i = 0; i < tabRects.length; i++) {
    const midpoint = tabRects[i].left + tabRects[i].width / 2;
    if (pointerX < midpoint) return i;
  }
  return tabRects.length;
}

/**
 * Pure drag-end resolution logic. Called from `PanelLayout.handleDragEnd` and
 * directly in unit tests.
 *
 * @param item   - The dragged item's data (may be undefined if drag was cancelled)
 * @param zone   - The drop target's data (may be undefined if dropped outside a zone)
 * @param newIndex - Pre-computed target index (used only for same-panel reorder)
 * @param reorderTab - Session action for within-panel reorder
 * @param moveTab    - Session action for cross-panel move
 */
export function resolveDragEnd(
  item: DraggableTabData | undefined,
  zone: DroppableTabBarData | undefined,
  newIndex: number,
  reorderTab: (tabId: string, panelId: string, index: number) => void,
  moveTab: (tabId: string, fromPanelId: string, toPanelId: string, atIndex: number) => void,
): void {
  if (!item || item.type !== 'tab') return;
  if (!zone || zone.type !== 'tabbar') return;

  if (zone.panelId === item.fromPanelId) {
    reorderTab(item.tabId, item.fromPanelId, newIndex);
  } else {
    moveTab(item.tabId, item.fromPanelId, zone.panelId, newIndex);
  }
}
